import { type MouseEvent, Fragment, ReactNode, useState } from 'react';
import { TextSmRg, Typography } from '@/UI/AppTypography/AppTypography';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { Box } from '@/UI/Box/Box';
import { log } from '@/logger/logger.ts';
import { FlexCenVer } from '@/UI/styles';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { MacCmdIcon } from '@/UI/Icons/MacCmdIcon';
import { color } from '@/colors';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { getOS } from '@/utils/general';
import { MenuV2Items, ShortcutContent, CursorMenuTooltip } from '@/UI/AppContextMenu/MenuV2Items';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { createTrackedMenuAction } from './createTrackedMenuAction';
import type { SxProps } from '@/UI/theme.exports';

const logger = log.getLogger('Actions');

export type DividerMenuItem = {
  type: 'divider';
};

export type SectionLabel = {
  type: 'section_label';
  text: string;
};

export type Slot = {
  type: 'slot';
  el: ReactNode;
};

export type RadioGroupMenuItem = {
  type: 'radio_group';
  value: string;
  options: Array<{ value: string; label: ReactNode; disabled?: boolean }>;
  onChange: (value: string, e: MouseEvent<HTMLElement>) => void;
  ariaLabel?: string;
  title?: ReactNode;
};

export type GroupMenuItem = {
  type: 'group';
  title?: ReactNode;
  ariaLabel?: string;
  items: MenuAction[];
};

export type CheckboxGroupMenuItem = {
  type: 'checkbox_group';
  title?: ReactNode;
  ariaLabel?: string;
  selectedValues: string[];
  onChange: (values: string[], e: MouseEvent<HTMLElement>) => void;
  options: Array<{ value: string; label: ReactNode; disabled?: boolean; icon?: ReactNode }>;
};

export type MenuActionKey = {
  menu: string;
  action: string;
};

export type ActionMenuItem = {
  action: (e: MouseEvent<HTMLElement>) => void | Promise<void>;
  /** Opt in to BI logging. Use nonempty menu and action names that uniquely identify each item definition. */
  biEventKey?: MenuActionKey;
  disabled?: boolean;
  /** Shown on hover while `disabled` (disabled rows otherwise swallow pointer events). */
  disabledTooltip?: ReactNode;
  /** Opt-in presentation used only by the flag-gated collapse action. */
  collapsePresentation?: boolean;
  onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLElement>) => void;
  name: ReactNode;
  endContent?: ReactNode;
  shortcut?: ReactNode;
  shortcutKey?: string;
  icon?: ReactNode;
  withLoading?: boolean;
  children?: MenuAction[];
  selected?: boolean;
  sx?: SxProps;
};

export type MenuAction =
  ActionMenuItem | RadioGroupMenuItem | GroupMenuItem | CheckboxGroupMenuItem | DividerMenuItem | SectionLabel | Slot;

export const isDividerMenuItem = (item: MenuAction): item is DividerMenuItem =>
  'type' in item && item.type === 'divider';

export const isSectionLabel = (item: MenuAction): item is SectionLabel =>
  'type' in item && item.type === 'section_label';

export const isSlot = (item: MenuAction): item is Slot => 'type' in item && item.type === 'slot';

export const isRadioGroupMenuItem = (item: MenuAction): item is RadioGroupMenuItem =>
  'type' in item && item.type === 'radio_group';

export const isGroupMenuItem = (item: MenuAction): item is GroupMenuItem => 'type' in item && item.type === 'group';

export const isCheckboxGroupMenuItem = (item: MenuAction): item is CheckboxGroupMenuItem =>
  'type' in item && item.type === 'checkbox_group';

export const isActionMenuItem = (item: MenuAction): item is ActionMenuItem => !('type' in item);
type ActionsP = {
  items: MenuAction[];
  setAnchorEl?: (anchorEl: null) => void;
  onClose?: (e?: MouseEvent<HTMLElement>) => void;
  fontSize?: string;
};

const Actions = ({ items, setAnchorEl, onClose, fontSize = '0.75rem' }: ActionsP) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const isMac = getOS() === 'Mac';

  const onClick = async (e: MouseEvent<HTMLElement>, item: ActionMenuItem, key: string) => {
    e.stopPropagation();
    // Disabled rows can still receive bubbled clicks from a tooltip hover overlay (pointer-events:auto).
    if (item.disabled) {
      return;
    }

    try {
      const action = createTrackedMenuAction(item);
      if (item.withLoading) {
        setLoadingKey(key);
        await action(e);
      } else {
        void action(e);
      }
    } catch (e) {
      logger.error('Error in menu actions', e);
    } finally {
      setLoadingKey(null);
    }
    setAnchorEl?.(null);
    onClose?.(e);
  };

  const renderShortcut = (item: MenuAction) => {
    if (!isActionMenuItem(item)) {
      return null;
    }

    if (item.shortcutKey) {
      return (
        <FlexCenVer sx={{ gap: 0.25 }}>
          {isMac ? <MacCmdIcon width={10} height={10} /> : <TextSmRg color={color.White64_T}>Ctrl</TextSmRg>}
          <TextSmRg color={color.White64_T}>{item.shortcutKey}</TextSmRg>
        </FlexCenVer>
      );
    }

    if (!item.shortcut) {
      return null;
    }

    if (typeof item.shortcut !== 'string') {
      return item.shortcut;
    }

    return (
      <TextSmRg fontStyle="italic" color={color.White64_T}>
        {item.shortcut}
      </TextSmRg>
    );
  };

  if (isFplMigrationEnabled) {
    return <MenuV2Items items={items} requestClose={() => onClose?.()} />;
  }

  const renderHeading = (text: ReactNode, key?: string) => (
    <Typography key={key} component="div" variant="label-xs-rg" color={color.White64_T} pl={1} py={0.5}>
      {text}
    </Typography>
  );

  const renderGroupHeader = (title: ReactNode, index: number) => (
    <>
      {index > 0 && <AppDivider />}
      {title != null && renderHeading(title)}
    </>
  );

  const renderItem = (item: MenuAction, index: number, keyPrefix = ''): ReactNode => {
    const key = `${keyPrefix}${index}`;

    if (isDividerMenuItem(item)) {
      return <AppDivider key={key} />;
    }

    if (isSectionLabel(item)) {
      return renderHeading(item.text, key);
    }

    if (isSlot(item)) {
      return <Fragment key={key}>{item.el}</Fragment>;
    }

    if (isGroupMenuItem(item)) {
      return (
        <Fragment key={key}>
          {renderGroupHeader(item.title, index)}
          {item.items.map((child, childIndex) => renderItem(child, childIndex, `${key}.`))}
        </Fragment>
      );
    }

    if (isCheckboxGroupMenuItem(item)) {
      return (
        <Fragment key={key}>
          {renderGroupHeader(item.title, index)}
          {item.options.map((opt) => {
            const checked = item.selectedValues.includes(opt.value);
            const next = checked
              ? item.selectedValues.filter((v) => v !== opt.value)
              : [...item.selectedValues, opt.value];
            return (
              <AppMenuItem
                key={opt.value}
                selected={checked}
                disabled={opt.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  item.onChange(next, e);
                }}
                sx={{ fontSize }}
              >
                <FlexCenVer sx={{ maxWidth: '100%' }}>
                  {opt.icon ? opt.icon : null}
                  <Box sx={{ ml: opt.icon ? 1 : 'auto', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {opt.label}
                  </Box>
                </FlexCenVer>
                {checked && <CheckMarkIcon />}
              </AppMenuItem>
            );
          })}
        </Fragment>
      );
    }

    if (isRadioGroupMenuItem(item)) {
      return (
        <Fragment key={key}>
          {renderGroupHeader(item.title, index)}
          {item.options.map((opt) => {
            const isSelected = item.value === opt.value;
            return (
              <AppMenuItem
                key={opt.value}
                selected={isSelected}
                disabled={opt.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  item.onChange(opt.value, e);
                }}
                sx={{ fontSize }}
              >
                <FlexCenVer sx={{ maxWidth: '100%' }}>
                  <Box sx={{ ml: 'auto', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</Box>
                </FlexCenVer>
                {isSelected && <CheckMarkIcon />}
              </AppMenuItem>
            );
          })}
        </Fragment>
      );
    }

    return (
      <AppMenuItem
        selected={item.selected}
        key={key}
        sx={{
          fontSize,
          ...(item.sx || {}),
          // Host an absolute overlay below so disabled tooltips can hear hover (MUI sets pointer-events:none).
          ...(item.disabled && item.disabledTooltip ? { position: 'relative' } : null),
        }}
        onClick={(e) => void onClick(e, item, key)}
        onMouseEnter={item.onMouseEnter}
        onMouseLeave={item.onMouseLeave}
        disabled={item.disabled}
      >
        <FlexCenVer sx={{ maxWidth: '100%' }}>
          {item.icon ? item.icon : null}
          <Box
            sx={{
              ml: item.icon ? 1 : 'auto',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.name}
          </Box>
        </FlexCenVer>
        {loadingKey === key ? <CircularProgress size={16} /> : item.endContent}
        {renderShortcut(item)}
        {item.selected &&
          (item.collapsePresentation ? (
            <Box component="span" sx={{ ml: 'auto', display: 'inline-flex' }}>
              <ShortcutContent data-collapse-check>
                <CheckMarkIcon />
              </ShortcutContent>
            </Box>
          ) : (
            <CheckMarkIcon />
          ))}
        {item.disabled && item.disabledTooltip ? (
          <DisabledActionTooltip item={item}>
            <Box
              component="span"
              aria-hidden
              // Keep hover for the tooltip, but don't let clicks undo MUI's disabled guard.
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }}
            />
          </DisabledActionTooltip>
        ) : null}
      </AppMenuItem>
    );
  };

  return items.map((item, index) => renderItem(item, index));
};

export default Actions;

/** Keep the existing tooltip for other actions; the collapse row follows the pointer. */
function DisabledActionTooltip({ item, children }: { item: ActionMenuItem; children: React.ReactElement }) {
  return item.collapsePresentation ? (
    <CursorMenuTooltip title={item.disabledTooltip}>{children}</CursorMenuTooltip>
  ) : (
    <Tooltip title={item.disabledTooltip} placement="top">
      {children}
    </Tooltip>
  );
}
